import type { DealIntakePollStatus } from '@/types/api'

export type IntakeTone = 'healthy' | 'busy' | 'warning'

export interface IntakeSummary {
  tone: IntakeTone
  label: string
  mailbox: string
  detail: string
}

export function relativeTime(iso: string | number, now: number): string {
  const then = typeof iso === 'number' ? iso : Date.parse(iso)
  const minutes = Math.max(0, Math.round((now - then) / 60_000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

function exactTime(value: Date, now: number): string {
  const sameDay = new Date(now).toDateString() === value.toDateString()
  return value.toLocaleString(undefined, {
    ...(sameDay ? {} : { dateStyle: 'medium' }),
    timeStyle: 'short',
  })
}

export function summarizeIntake(
  statuses: readonly DealIntakePollStatus[],
  issueCount: number,
  processingCount: number,
  now: number
): IntakeSummary | null {
  if (statuses.length === 0) return null

  const status = statuses[0]
  const lastSuccess = status.lastSucceededAt
    ? new Date(status.lastSucceededAt)
    : null
  const scheduled = statuses.some((folder) => folder.scheduled)
  const failing = statuses.some((folder) => !folder.healthy)
  const stale = statuses.some((folder) => folder.stale)

  const when = lastSuccess
    ? relativeTime(lastSuccess.toISOString(), now)
    : 'never'
  const detail = [
    lastSuccess ? `Checked ${exactTime(lastSuccess, now)}` : 'Never checked',
    scheduled
      ? `checks every ${status.intervalMinutes} minutes`
      : 'automatic checks are off',
  ].join(' · ')

  if (issueCount > 0) {
    return {
      tone: 'warning',
      label: `${issueCount} ${issueCount === 1 ? 'issue' : 'issues'}`,
      mailbox: status.mailbox,
      detail,
    }
  }

  if (failing)
    return {
      tone: 'warning',
      label: 'Checks failing',
      mailbox: status.mailbox,
      detail,
    }

  // Above staleness, below failure: work in flight is why nothing was checked lately.
  if (processingCount > 0)
    return {
      tone: 'busy',
      label: `Reading ${processingCount} ${processingCount === 1 ? 'email' : 'emails'}`,
      mailbox: status.mailbox,
      detail,
    }

  if (stale)
    return {
      tone: 'warning',
      label: `Last checked ${when}`,
      mailbox: status.mailbox,
      detail,
    }

  return {
    tone: 'healthy',
    label: 'Up to date',
    mailbox: status.mailbox,
    detail,
  }
}
